import { api } from '../../../lib/api/client';

export interface AnswerCallResponse {
  callId: string;
  status: 'answered';
  transport: string;
  answeredAt: string;
}

export interface EndCallResponse {
  callId: string;
  status: 'completed';
  durationSeconds: number;
}

export interface UploadRecordingResponse {
  recordingUrl: string | null;
  recordingStatus: 'ready' | 'disabled';
}

export interface CallRecordingFiles {
  mixed: Blob;
  staff: Blob | null;
  customer: Blob | null;
}

export async function uploadCallRecording(
  callId: string,
  files: CallRecordingFiles,
  durationMs: number
): Promise<UploadRecordingResponse> {
  const form = new FormData();
  form.append('file', files.mixed, `call-${callId}.webm`);
  if (files.staff) {
    form.append('staff_audio', files.staff, `call-${callId}-staff.webm`);
  }
  if (files.customer) {
    form.append(
      'customer_audio',
      files.customer,
      `call-${callId}-customer.webm`
    );
  }
  form.append('duration_ms', String(Math.max(0, Math.round(durationMs))));

  // Pass FormData through axios — let the browser set the multipart boundary
  // by leaving Content-Type undefined. Override the JSON default header.
  return api.post<UploadRecordingResponse>(
    `/voice/calls/${callId}/recording`,
    form,
    { headers: { 'Content-Type': undefined } }
  );
}

export interface TwilioConfig {
  account_sid: string;
  auth_token: string;       // backend returns '***' when set, '' when unset
  api_key_sid: string;
  api_key_secret: string;   // backend returns '***' when set, '' when unset
  phone_number: string;
  sip_domain: string;
}

export interface WhatsappSipState {
  configured_at: string | null;
  active: boolean;
  auth_mode: 'credential' | 'ip_whitelist';
}

// SIP-trunk transport via a Jambonz SBC (ipnx / didww / any carrier). The path
// for Nigerian PSTN, where Twilio has no DIDs. Carrier is config, not code.
export const SIP_PROVIDERS = ['ipnx', 'didww', 'other'] as const;
export type SipProvider = (typeof SIP_PROVIDERS)[number];

export interface SipConfig {
  provider: string;            // ipnx | didww | other (informational)
  did: string;                 // the dialed number this store owns
  jambonz_base_url: string;
  jambonz_account_sid: string;
  jambonz_api_key: string;     // backend returns '***' when set, '' when unset
  // Carrier (trunk) registration creds — for ipnx / DIDWW-registered. Leave
  // blank for IP-auth (DIDWW static).
  carrier_sip_realm: string;   // e.g. sip.ipnxtelecoms.com
  carrier_username: string;    // ipnx "SIP LINE"
  carrier_password: string;    // '***' when set, '' when unset
  carrier_ip_allowlist: string[]; // carrier inbound IPs (ipnx whitelisting)
  didww_api_key: string;       // '***' when set, '' when unset
  jambonz_sip_fqdn: string;    // e.g. yoursub.sip.jambonz.cloud
  jambonz_sbc_ips: string[];   // Jambonz outbound SIP IPs (DIDWW voice_out_trunk)
}

export interface DidwwProvisionResult {
  ok: boolean;
  provisioned: {
    did: string;
    did_id: string;
    voice_in_trunk_id: string;
    trunk_group_id: string;
    delivery_host: string;
  };
}

export interface SipProvisionResult {
  ok: boolean;
  provisioned: {
    application_sid: string;
    voip_carrier_sid: string;
    phone_number_sid: string;
    auth: 'registration' | 'ip';
    gateways_created: number;
    did: string;
    call_hook_url: string;
  };
}

export interface UseIpWhitelistResponse {
  settings: VoiceSettings;
  result: {
    ip_acl_created: boolean;
    addresses_added: string[];
    addresses_already_present: number;
    ip_acl_attached: boolean;
    credential_list_detached: boolean;
  };
}

export interface AiAgentState {
  enabled: boolean;
  voice: string;
  greeting: string;
  max_call_duration_seconds: number;
  // Voice-layer providers (voice models do voice only; LangGraph is the brain).
  stt_provider: SttProvider;
  tts_provider: TtsProvider;
  elevenlabs_voice_id: string;
}

// Swappable voice-slot providers. Default OpenAI (cheapest); ElevenLabs (TTS)
// and Deepgram (STT) are premium opt-ins. Kept in sync with STT_PROVIDERS /
// TTS_PROVIDERS in app/api/voice.py.
export const STT_PROVIDERS = ['openai', 'deepgram'] as const;
export const TTS_PROVIDERS = ['openai', 'elevenlabs'] as const;
export type SttProvider = (typeof STT_PROVIDERS)[number];
export type TtsProvider = (typeof TTS_PROVIDERS)[number];

// Rough per-call-minute voice-layer cost, shown next to the provider choice so
// the quality/cost tradeoff is visible. See docs/roadmap-ai-receptionist.md.
export const PROVIDER_COST_HINT: Record<string, string> = {
  openai: '≈ $0.012/min',
  elevenlabs: '≈ $0.032/min',
  deepgram: '≈ $0.008/min',
};

// OpenAI Realtime voice options (kept in sync with backend
// REALTIME_VOICES in app/api/voice.py).
export const REALTIME_VOICES = [
  'alloy',
  'ash',
  'ballad',
  'coral',
  'echo',
  'sage',
  'shimmer',
  'verse',
] as const;

export type VoiceTransport = 'webrtc' | 'twilio' | 'sip';

export interface VoiceSettings {
  transport: VoiceTransport;
  recording_enabled: boolean;
  twilio: TwilioConfig;
  sip: SipConfig;
  whatsapp_sip: WhatsappSipState;
  ai_agent: AiAgentState;
}

export interface VoiceSettingsPatch {
  transport?: VoiceTransport;
  recording_enabled?: boolean;
  twilio?: Partial<TwilioConfig>;
  sip?: Partial<SipConfig>;
  ai_agent?: Partial<AiAgentState>;
}

// Appointment booking settings (store.config['booking']). Available to all
// tenants, off by default. Mirrors StoreBookingSettings in app/api/schemas.py.
export interface BookingSettings {
  enabled: boolean;
  services: string[];
}

export interface TransferableStaff {
  id: string;
  name: string;
  online: boolean;
}

export interface TwilioToken {
  token: string;
  identity: string;
  expiresAt: string;
}

export interface TransferCallResponse {
  callId: string;
  status: 'completed';
  endReason: 'transferred';
  assignedStaffId: string;
  assignedStaffName: string;
}

export const voiceService = {
  answerCall(callId: string, sdpAnswer: string): Promise<AnswerCallResponse> {
    return api.post<AnswerCallResponse>(`/voice/calls/${callId}/answer`, {
      sdpAnswer,
    });
  },

  endCall(callId: string): Promise<EndCallResponse> {
    return api.post<EndCallResponse>(`/voice/calls/${callId}/end`);
  },

  uploadCallRecording,

  getSettings(): Promise<VoiceSettings> {
    return api.get<VoiceSettings>('/voice/settings');
  },

  updateSettings(patch: VoiceSettingsPatch): Promise<VoiceSettings> {
    return api.patch<VoiceSettings>('/voice/settings', patch);
  },

  // Appointment booking settings (store-wide; available to all tenants, off by
  // default). Lives on the stores router; gates the agent's book_appointment tool.
  getBookingSettings(): Promise<BookingSettings> {
    return api.get<BookingSettings>('/stores/booking-settings');
  },

  updateBookingSettings(patch: Partial<BookingSettings>): Promise<BookingSettings> {
    return api.patch<BookingSettings>('/stores/booking-settings', patch);
  },

  // One-click provision the store's SIP trunk in Jambonz from saved settings.
  configureSip(): Promise<SipProvisionResult> {
    return api.post<SipProvisionResult>('/voice/sip/configure');
  },

  // One-click point the store's DIDWW number at Jambonz from saved settings.
  configureDidww(): Promise<DidwwProvisionResult> {
    return api.post<DidwwProvisionResult>('/voice/sip/configure-didww');
  },

  listTransferableStaff(): Promise<TransferableStaff[]> {
    return api.get<TransferableStaff[]>('/voice/transferable-staff');
  },

  transferCall(callId: string, toStaffId: string): Promise<TransferCallResponse> {
    return api.post<TransferCallResponse>(`/voice/calls/${callId}/transfer`, {
      toStaffId,
    });
  },

  getTwilioToken(): Promise<TwilioToken> {
    return api.get<TwilioToken>('/voice/twilio-token');
  },

  configureWhatsappSip(): Promise<VoiceSettings> {
    return api.post<VoiceSettings>('/voice/whatsapp-sip/configure');
  },

  toggleWhatsappSip(active: boolean): Promise<VoiceSettings> {
    return api.patch<VoiceSettings>('/voice/whatsapp-sip', { active });
  },

  deleteWhatsappSip(): Promise<VoiceSettings> {
    return api.delete<VoiceSettings>('/voice/whatsapp-sip');
  },

  diagnoseWhatsappSip(): Promise<WhatsappSipDiagnosis> {
    return api.get<WhatsappSipDiagnosis>('/voice/whatsapp-sip/diagnose');
  },

  useIpWhitelistAuth(): Promise<UseIpWhitelistResponse> {
    return api.post<UseIpWhitelistResponse>('/voice/whatsapp-sip/use-ip-whitelist');
  },
};

export interface WhatsappSipDiagnosis {
  meta: {
    sip_status: 'ENABLED' | 'DISABLED' | null;
    has_password: boolean;
    expected_username: string;
    hostname: string | null;
    port: number | null;
    error: string | null;
  };
  twilio: {
    credential_list_exists: boolean;
    credential_for_user_exists: boolean;
    sip_domain_found: boolean;
    credential_list_attached_to_domain: boolean;
    voice_url_set: boolean;
    voice_url: string | null;
    expected_voice_url: string;
    error: string | null;
  };
  store_config: {
    sip_domain: string;
    whatsapp_number: string | null;
  };
}
